// Regras da lista de conversas que PRECISAM bater com o ListTicketsService.
//
// A lista nasce da consulta, mas vive de socket: todo ticket que chega por evento
// é julgado aqui, no navegador. Se a regra daqui for mais frouxa que o SQL, entra
// na tela conversa que a consulta nunca traria; se for mais dura, a conversa some
// até alguém filtrar de novo. Mudou um filtro no backend, muda aqui também.
import { format, parseISO } from "date-fns";

const MINUTOS_ESPERA = { "30m": 30, "1h": 60, "2h": 120, "3h": 180 };

// Filtro que depende do relógio: o ticket passa a caber sem evento nenhum, só
// porque o tempo passou. A lista precisa se rebuscar sozinha enquanto ele vale.
export const ehFiltroPorTempo = selectedTime => !!MINUTOS_ESPERA[selectedTime];

const incluiId = (lista, id) =>
	id !== null && id !== undefined && lista.some(item => Number(item) === Number(id));

// Mesmo recorte do TicketController.index: a seleção vale só dentro das conexões
// do usuário, e seleção vazia (ou que não sobrou nada) vira todas as dele.
const conexoesEfetivas = (selecionadas, user) => {
	const doUsuario = (user?.whatsapps || []).map(w => Number(w.id));
	const pedidas = (selecionadas || [])
		.map(Number)
		.filter(id => doUsuario.includes(id));
	return pedidas.length > 0 ? pedidas : doUsuario;
};

// DATE(dataUltimaMensagem) BETWEEN dataInicio AND dataFim, com as duas pontas
// obrigatórias — só uma preenchida não filtra, igual ao backend.
const dentroDoPeriodo = (dataUltimaMensagem, dataInicio, dataFim) => {
	if (!dataInicio || !dataFim) return true;
	if (!dataUltimaMensagem) return false;
	const dia = format(parseISO(dataUltimaMensagem), "yyyy-MM-dd");
	return dia >= dataInicio && dia <= dataFim;
};

// "Nao lidos"/"Lidos" são da aba Grupos, que filtra por leitura em vez de resposta.
const passaFiltroResposta = (selectedTime, ticket, agora) => {
	const minutos = MINUTOS_ESPERA[selectedTime];
	if (minutos) {
		if (!ticket.naoRespondido || !ticket.dataUltimaMensagem) return false;
		return new Date(ticket.dataUltimaMensagem) < new Date(agora.getTime() - minutos * 60 * 1000);
	}
	switch (selectedTime) {
		case "Aguardando resposta": return !!ticket.naoRespondido;
		case "Respondido": return !ticket.naoRespondido;
		case "Aguardando 1 interacao": return !!ticket.aguardandoPrimeiraInteracao;
		case "Nao lidos": return (ticket.unreadMessages || 0) > 0;
		case "Lidos": return !((ticket.unreadMessages || 0) > 0);
		default: return true;
	}
};

// O ticket entra nesta lista? `filtros` são as props do TicketsList (status é a
// aba), `user` é o logado e `agora` vem no relógio do servidor, o mesmo do SQL.
//
// Contatos: etapa obrigatória e marcada (queueId IN não casa NULL), e a etapa tem
// que aparecer na lista de mensagens — mas só se o include for da etapa atual, que
// num payload de socket pode ter ficado da anterior. Aguardando: userFilial é
// permissão, não filtro da tela — quem é preso à filial vê a dela e a sem filial.
export const ticketCabeNaLista = (ticket, filtros, { user, agora = new Date() } = {}) => {
	if (!ticket) return false;
	const { status } = filtros;

	if (status === "grupos") {
		if (!ticket.isGroup) return false;
	} else if (ticket.isGroup || ticket.status !== status) {
		return false;
	}

	if (!incluiId(conexoesEfetivas(filtros.selectedWhatsappIds, user), ticket.whatsappId)) {
		return false;
	}

	if (status === "open") {
		if (!incluiId(filtros.selectedQueueIds || [], ticket.queueId)) return false;
		if (ticket.queue && Number(ticket.queue.id) === Number(ticket.queueId)
			&& !ticket.queue.mostrarFilaMensagens) {
			return false;
		}
		if (user?.gestor) {
			if (filtros.selectedUserIds && !incluiId(filtros.selectedUserIds, ticket.userId)) return false;
			if (filtros.selectedFilialIds && !incluiId(filtros.selectedFilialIds, ticket.filial_icode)) return false;
		} else if (Number(ticket.userId) !== Number(user?.id)) {
			return false;
		}
	} else if (status === "pending") {
		if (ticket.aguardandoResposta || ticket.pesquisaSatisfacao) return false;
		if (ticket.queueId !== null && ticket.queueId !== undefined
			&& !incluiId(filtros.selectedQueueIds || [], ticket.queueId)) {
			return false;
		}
		if (user?.userFilial && ticket.filial_icode !== null && ticket.filial_icode !== undefined
			&& Number(ticket.filial_icode) !== Number(user.filial_icode)) {
			return false;
		}
		if (user?.gestor && filtros.selectedUserIds && !incluiId(filtros.selectedUserIds, ticket.userId)) {
			return false;
		}
	}

	if (filtros.selectedTag && Number(filtros.selectedTag) !== Number(ticket.tagId)) return false;
	if (!dentroDoPeriodo(ticket.dataUltimaMensagem, filtros.dataInicio, filtros.dataFim)) return false;
	return passaFiltroResposta(filtros.selectedTime, ticket, agora);
};

// Mesma ordem do ORDER BY: (não lidas), última mensagem, id — NULL por último.
const compararTickets = ordenarPorNaoLidas => (a, b) => {
	if (ordenarPorNaoLidas) {
		const porNaoLidas = (b.unreadMessages || 0) - (a.unreadMessages || 0);
		if (porNaoLidas !== 0) return porNaoLidas;
	}
	const da = a.dataUltimaMensagem ? new Date(a.dataUltimaMensagem).getTime() : null;
	const db = b.dataUltimaMensagem ? new Date(b.dataUltimaMensagem).getTime() : null;
	if (da !== db) {
		if (da === null) return 1;
		if (db === null) return -1;
		return db - da;
	}
	return Number(b.id) - Number(a.id);
};

const comoTicket = cursor => ({
	id: cursor.id,
	dataUltimaMensagem: cursor.d,
	unreadMessages: cursor.u
});

const instante = valor => (valor ? new Date(valor).getTime() : 0);

// A versão da tela é mais nova que a que chegou? Só compara quando as duas trazem
// updatedAt; payload sem ele é aplicado.
const maisNovo = (a, b) =>
	!!(a?.updatedAt && b?.updatedAt) && instante(a.updatedAt) > instante(b.updatedAt);

// Teto de conversas na tela: gestor com todas as conexões e a tela aberta o dia todo
// chega a milhares e trava o navegador. O que passa dele sai por baixo e a lista
// ganha "mais" a partir da última que ficou, então rolando as cortadas voltam. O que
// o atendente carrega rolando sobe o teto — só o que chega sozinho é cortado.
export const LIMITE_TELA = 100;

// `cursor`/`temMais` moram junto dos tickets porque o corte mexe nos três de uma vez.
export const LISTA_VAZIA = { tickets: [], removidos: {}, cursor: null, temMais: false, limite: LIMITE_TELA };

const chaveDoTicket = (ticket, ordenarPorNaoLidas) => ({
	d: ticket.dataUltimaMensagem ? new Date(ticket.dataUltimaMensagem).toISOString() : null,
	id: ticket.id,
	...(ordenarPorNaoLidas ? { u: ticket.unreadMessages || 0 } : {}),
});

// O cursor vai para a última que ficou, a não ser que o atual venha antes dela
// (ticket de socket inserido além do que a consulta já entregou): aí o trecho
// entre os dois nunca veio do banco e não pode ser pulado.
const aparar = (estado, ordenarPorNaoLidas) => {
	const limite = estado.limite || LIMITE_TELA;
	if (estado.tickets.length <= limite) return estado;
	const tickets = estado.tickets.slice(0, limite);
	const ultimo = tickets[tickets.length - 1];
	const cursorAtualVemAntes = estado.temMais && estado.cursor
		&& compararTickets(ordenarPorNaoLidas)(comoTicket(estado.cursor), ultimo) < 0;
	return {
		...estado,
		tickets,
		temMais: true,
		cursor: cursorAtualVemAntes ? estado.cursor : chaveDoTicket(ultimo, ordenarPorNaoLidas),
	};
};

// Quanto tempo uma saída por evento continua barrando resposta velha. Bem acima
// do timeout de 180 s do axios: nenhuma consulta dura mais que isso.
const VALIDADE_REMOCAO_MS = 15 * 60 * 1000;

const registrarRemocao = (removidos, id, quando) => {
	const novo = {};
	Object.keys(removidos).forEach(chave => {
		if (removidos[chave] > quando - VALIDADE_REMOCAO_MS) novo[chave] = removidos[chave];
	});
	novo[id] = quando;
	return novo;
};

const esquecerRemocao = (removidos, id) => {
	if (!(id in removidos)) return removidos;
	const novo = { ...removidos };
	delete novo[id];
	return novo;
};

// Junta uma página da consulta à lista da tela. `modo` é o da busca: "inicio",
// "mais" (rolagem) ou "ressincronizar" (rebusca do topo).
//
// A página responde por um TRECHO da ordem: do cursor pedido até a última linha que
// veio (ou até o fim). Dentro dele a consulta é a verdade: ticket da tela que ficou
// de fora sai, a não ser que tenha mudado depois dela (updatedAt > consultadoEm). E
// ticket que saiu por evento depois da consulta (`removidos`) não volta. Resposta sem
// consultadoEm (backend anterior a ele) só acrescenta e atualiza.
//
// A rebusca responde só pelo topo: com a lista já rolada além dele, o cursor da
// rolagem continua valendo.
export const mesclarPagina = (estado, pagina, {
	modo,
	cursorPedido,
	cursorRecebido,
	temMais,
	consultadoEm,
	ordenarPorNaoLidas
}) => {
	const comparar = compararTickets(ordenarPorNaoLidas);
	const limiteConsulta = consultadoEm ? instante(consultadoEm) : null;
	const inicio = cursorPedido ? comoTicket(cursorPedido) : null;
	const fim = cursorRecebido ? comoTicket(cursorRecebido) : null;
	const noTrecho = ticket =>
		(!inicio || comparar(inicio, ticket) < 0) && (!fim || comparar(ticket, fim) <= 0);

	const idsPagina = new Set(pagina.map(t => t.id));
	const porId = new Map();
	estado.tickets.forEach(ticket => {
		const saiu = limiteConsulta !== null && noTrecho(ticket) && !idsPagina.has(ticket.id)
			&& instante(ticket.updatedAt) <= limiteConsulta;
		if (!saiu) porId.set(ticket.id, ticket);
	});
	pagina.forEach(ticket => {
		if (limiteConsulta !== null && estado.removidos[ticket.id] > limiteConsulta) return;
		const atual = porId.get(ticket.id);
		if (!atual || !maisNovo(atual, ticket)) porId.set(ticket.id, ticket);
	});
	const tickets = [...porId.values()].sort(comparar);
	const mantemRolagem = modo === "ressincronizar" && temMais && estado.temMais;
	const novo = {
		...estado,
		tickets,
		cursor: mantemRolagem ? estado.cursor : (cursorRecebido || null),
		temMais: mantemRolagem ? estado.temMais : !!temMais,
		limite: modo === "mais" ? Math.max(estado.limite || LIMITE_TELA, tickets.length) : estado.limite,
	};
	return modo === "mais" ? novo : aparar(novo, ordenarPorNaoLidas);
};

// Ticket que chegou por socket: entra (no lugar certo da ordem), é atualizado ou
// sai, conforme cabe no filtro. Evento atrasado não sobrescreve versão mais nova.
// `quando` é o agora no relógio do servidor, para o payload que vier sem updatedAt.
export const aplicarTicket = (estado, ticket, cabe, { ordenarPorNaoLidas, quando }) => {
	const atual = estado.tickets.find(t => t.id === ticket.id);
	if (atual && maisNovo(atual, ticket)) return estado;
	const semEle = estado.tickets.filter(t => t.id !== ticket.id);
	if (!cabe) {
		return {
			...estado,
			tickets: atual ? semEle : estado.tickets,
			removidos: registrarRemocao(estado.removidos, ticket.id, instante(ticket.updatedAt) || quando)
		};
	}
	return aparar({
		...estado,
		tickets: [...semEle, ticket].sort(compararTickets(ordenarPorNaoLidas)),
		removidos: esquecerRemocao(estado.removidos, ticket.id)
	}, ordenarPorNaoLidas);
};

export const removerTicket = (estado, ticketId, quando) => ({
	...estado,
	tickets: estado.tickets.filter(t => t.id !== ticketId),
	removidos: registrarRemocao(estado.removidos, ticketId, quando)
});

// O evento de leitura só traz o id. A cópia da tela ganha o agora do servidor
// como updatedAt, senão uma resposta anterior à leitura devolveria a bolinha.
// Depois de zerada ela pode deixar de caber ("Não lidos").
export const marcarLido = (estado, ticketId, avaliar, quando) => {
	const atual = estado.tickets.find(t => t.id === ticketId);
	if (!atual) return estado;
	const lido = { ...atual, unreadMessages: 0, updatedAt: new Date(quando).toISOString() };
	if (!avaliar(lido)) return removerTicket(estado, ticketId, quando);
	return { ...estado, tickets: estado.tickets.map(t => (t.id === ticketId ? lido : t)) };
};

export const atualizarContato = (estado, contato) => ({
	...estado,
	tickets: estado.tickets.map(t => (t.contactId === contato.id ? { ...t, contact: contato } : t))
});

// Sem página por carregar, o que está na tela é o total. Com página, vale a contagem
// do servidor (GET /tickets/contagem, que para no teto e diz `excedeu`); sem ela, ou
// com ela atrás do que já está na tela, "N+".
export const rotuloContagem = (quantidade, temMais, contagem) => {
	if (!temMais) return quantidade;
	if (contagem && contagem.total > quantidade) {
		return contagem.excedeu ? `${contagem.total}+` : contagem.total;
	}
	return `${quantidade}+`;
};
